import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Space, Input, Form, Row, Col, Tag, DatePicker, Tooltip, Modal, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteNotificationRequest, getListNotificationsRequest } from '../../services/notificationRequest';
import { getListCampaignsCMSNextRequest } from '../../services/commonRequest';
import { TableCustomV3 } from '../../components/TableCustomV3';
import { LIST_CATEGORY_NOTIFICATION, LIST_STATUS_NOTIFICATION } from '../../constants/appSelect';
import { newObjectKeys } from '../../utils/utility';
import { checkPermission } from '../../utils/checkPermission';
import { CREATE_UPDATE_NOTIFICATION, DELETE_NOTIFICATION, GET_LIST_NOTIFICATIONS } from '../../constants/apiEndPoint';
import { isEqual } from 'lodash';
const { RangePicker } = DatePicker;

const { Text } = Typography;

const QuanLyThongBao = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    pageId: 1,
    limit: 20,
    status: [0, 1, 3, 4, 5, 6, 7],
  });

  const { data: dataAllCampaignsCMSNext } = useQuery({
    queryKey: ['dataAllCampaignsCMSNext'],
    queryFn: () => getListCampaignsCMSNextRequest(),
  });

  const dataAllCampaignsCMSNextOption = useMemo(() => {
    return dataAllCampaignsCMSNext
      ?.filter((item) => item?.status === 1)
      ?.map((item) => ({
        label: item?.title,
        value: item?.id,
      })) || [];
  }, [dataAllCampaignsCMSNext]);

  const {
    data: dataNotifications,
    isPending,
    refetch: refetchDataNotifications,
  } = useQuery({
    queryKey: ['dataNotifications', params],
    queryFn: () => getListNotificationsRequest(params),
    enabled: checkPermission(GET_LIST_NOTIFICATIONS, 'Xem danh sách'),
  });

  useEffect(() => {
    const saved = sessionStorage.getItem('filterStateNotifications');
    if (saved) {
      const filters = JSON.parse(saved);
      let tempForm = filters;
      if (filters?.from_public_time && filters?.to_public_time) {
        tempForm = { ...tempForm, timeRange: [dayjs(filters?.from_public_time), dayjs(filters?.to_public_time)] };
      }
      formSearch.setFieldsValue(tempForm);
      setParams(filters);
    }
  }, []);

  // handle submit form
  const { mutate: mutateDeleteNotification } = useMutation({
    mutationFn: deleteNotificationRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa thông báo thành công',
        });
        refetchDataNotifications();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleConfirm = (record, actionType) => {
    let modalConfig = {};
    if (actionType === 'delete') {
      modalConfig = {
        content: 'Bạn có muốn xóa thông báo này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        okType: 'danger',
        onOk() {
          mutateDeleteNotification({ id: record?.id });
        },
        onCancel() {},
      };
    } else {
      return;
    }
    Modal.confirm(modalConfig);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      minWidth: 70,
      fixed: 'left',
    },
    {
      title: 'Tên thông báo',
      dataIndex: ['label', 'id'],
      key: 'id',

      render: (_, { label, id }) => (
        <Link to={`/quan-ly-thong-bao/${id}/chinh-sua-thong-bao`} className="text-[#1677FF] hover:cursor-pointer">
          {label}
        </Link>
      ),
      minWidth: 100,
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (description) => <Text>{description}</Text>,
      minWidth: 100,
      width: 150,
    },
    {
      title: 'Tập khách hàng',
      dataIndex: ['platform', 'version', 'topic', 'audience', 'type_topic'],
      key: ['audience', 'topic'],
      render: (_, { platform, version, topic, audience, type_topic }) => (
        <Space direction="vertical">
          {platform && <Text>{platform}</Text>}
          {version && Array.isArray(version) && version?.map((item) => <Text>{item}</Text>)}
          {topic && Array.isArray(topic) && topic?.map((item) => <Text>{item}</Text>)}
          {audience && Array.isArray(audience) && audience?.map((item) => <Text>{item}</Text>)}
        </Space>
      ),
    },
    {
      title: 'Loại thông báo',
      dataIndex: 'category_id',
      key: 'category_id',
      render: (category_id) => (
        <Text> {LIST_CATEGORY_NOTIFICATION.find((item) => item.value === category_id)?.label}</Text>
      ),
      width: 100,
    },
    {
      title: 'Deeplink',
      dataIndex: 'deeplink',
      key: 'deeplink',
      render: (deeplink) => <Text>{deeplink}</Text>,
      minWidth: 150,
      width: 350,
    },
    {
      title: 'Thời gian gửi',
      dataIndex: 'public_time',
      key: 'public_time',
      render: (public_time) => <Text>{dayjs(public_time * 1000).format('HH:mm:ss DD/MM/YYYY')}</Text>,
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (create_time) => <Text>{dayjs(create_time).format('HH:mm:ss DD/MM/YYYY')}</Text>,
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (last_updated) => <Text>{dayjs(last_updated).format('HH:mm:ss DD/MM/YYYY')}</Text>,
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_NOTIFICATION.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_NOTIFICATION.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: 100,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(CREATE_UPDATE_NOTIFICATION, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa thông báo">
              <EditOutlined onClick={() => navigate(`/quan-ly-thong-bao/${record?.id}/chinh-sua-thong-bao`)} />
            </Tooltip>
          )}
          {checkPermission(DELETE_NOTIFICATION, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa thông báo">
              <DeleteOutlined onClick={() => handleConfirm(record, 'delete')} />
            </Tooltip>
          )}
          {checkPermission(CREATE_UPDATE_NOTIFICATION, 'Sao chép') && (
            <Tooltip title="Sao chép thông báo">
              <CopyOutlined onClick={() => navigate(`/quan-ly-thong-bao/${record?.id}/sao-chep-thong-bao`)} />
            </Tooltip>
          )}
        </Space>
      ),
      fixed: 'right',
      hidden:
        !checkPermission(CREATE_UPDATE_NOTIFICATION, 'Chỉnh sửa') &&
        !checkPermission(DELETE_NOTIFICATION, 'Xóa') &&
        !checkPermission(CREATE_UPDATE_NOTIFICATION, 'Sao chép'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };
    if (values?.timeRange) {
      Object.assign(temp, {
        from_public_time: dayjs(values?.timeRange?.[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        to_public_time: dayjs(values?.timeRange?.[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
    }

    if (values?.label) {
      Object.assign(temp, { label: values?.label });
    }

    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }

    if (values?.category_id > 0) {
      Object.assign(temp, { category_id: values?.category_id });
    }

    if (values?.campaign_id) {
      Object.assign(temp, { campaign_id: values?.campaign_id });
    }

    if (values?.status?.length) {
      Object.assign(temp, { status: values?.status });
    } else {
      Object.assign(temp, { status: [0, 1, 3, 4, 5, 6, 7] });
    }

    if (isEqual(temp, params)) {
      refetchDataNotifications();
    } else {
      sessionStorage.setItem('filterStateNotifications', JSON.stringify(temp));
      setParams(temp);
    }
  };

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách thông báo</h1>
        {checkPermission(CREATE_UPDATE_NOTIFICATION, 'Tạo mới') && (
          <Button type="primary" onClick={() => navigate('/quan-ly-thong-bao/them-thong-bao')}>
            <PlusOutlined />
            Thêm thông báo
          </Button>
        )}
      </div>
      {checkPermission(GET_LIST_NOTIFICATIONS, 'Xem danh sách') && (
        <>
          <Form
            name="search-notification"
            form={formSearch}
            onFinish={handleSubmitForm}
            layout="vertical"
            className="mb-5"
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Thời gian gửi thông báo" />} name="timeRange">
                  <RangePicker placeholder={['Từ ngày', 'Đến ngày']} className="h-[40px] w-full" format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tên thông báo" />} name="label">
                  <Input placeholder="Nhập thông báo..." allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tiêu đề thông báo" />} name="title">
                  <Input placeholder="Nhập tiêu đề thông báo..." allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Loại thông báo" />} name="category_id">
                  <CustomSelect
                    placeholder="Chọn loại thông báo"
                    options={[{ value: -1, label: 'Tất cả' }, ...LIST_CATEGORY_NOTIFICATION]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Chiến dịch" />} name="campaign_id">
                  <CustomSelect placeholder="Chọn chiến dịch" allowClear options={dataAllCampaignsCMSNextOption} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status">
                  <CustomSelect placeholder="Chọn trạng thái" mode="multiple" options={LIST_STATUS_NOTIFICATION} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6} className="flex items-end">
                <Form.Item>
                  <Space direction="horizontal">
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined /> Tìm kiếm
                    </Button>
                    {params &&
                      Object.values(
                        newObjectKeys(params, ['start_time', 'end_time', 'label', 'title', 'category_id', 'status', 'campaign_id'])
                      ).some((value) => (value && !Array.isArray(value)) || value?.length > 0) && (
                        <Button
                          htmlType="button"
                          onClick={() => {
                            formSearch.resetFields();
                            sessionStorage.setItem(
                              'filterStateNotifications',
                              JSON.stringify({ pageId: 1, limit: params.limit, status: [0, 1, 3, 4, 5, 6, 7] })
                            );
                            setParams({
                              pageId: 1,
                              limit: params.limit,
                              status: [0, 1, 3, 4, 5, 6, 7],
                            });
                          }}
                        >
                          <DeleteOutlined />
                          Xóa lọc
                        </Button>
                      )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <TableCustomV3
            isLoading={isPending}
            columns={columns}
            dataSource={dataNotifications?.data}
            totalItem={dataNotifications?.totalItem}
            params={params}
            setParams={(params) => {
              setParams(params);
              sessionStorage.setItem('filterStateNotifications', JSON.stringify(params));
            }}
          />
        </>
      )}
    </div>
  );
};

export default QuanLyThongBao;
